<template>
  <div v-if="shouldRender" :class="sectionClass">
    <div v-if="showHeading" :class="headingWrapperClass">
      <p :class="headingClass">
        {{ heading }}
      </p>
    </div>

    <div :class="tagsAreaClass">
      <div
        v-if="loading && specialisations.length === 0"
        :class="loadingWrapperClass"
      >
        <p>{{ loadingLabel }}</p>
      </div>

      <div v-else :class="tagsRowClass">
        <button
          v-if="showAllOption"
          type="button"
          :class="pillClass(modelValue === null)"
          @click="select(null)"
        >
          <span :class="pillLabelClass">{{ t('common.all') }}</span>
        </button>

        <button
          v-for="specialisation in specialisations"
          :key="specialisation.id"
          type="button"
          :class="pillClass(modelValue === specialisation.id)"
          @click="select(specialisation.id)"
        >
          <span :class="pillLabelClass">{{ specialisation.name }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useTranslations } from '@/utils/translations';

const { t } = useTranslations();

const props = defineProps({
  specialisations: {
    type: Array,
    default: () => [],
  },
  modelValue: {
    type: [Number, String],
    default: null,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  heading: {
    type: String,
    default: 'Specjalizacje',
  },
  showHeading: {
    type: Boolean,
    default: true,
  },
  loadingLabel: {
    type: String,
    default: 'Ładowanie specjalizacji…',
  },
  /** `header` = MainHeader dropdown pills; `page` = resource index pages */
  variant: {
    type: String,
    default: 'header',
    validator: (value) => ['header', 'page'].includes(value),
  },
  showAllOption: {
    type: Boolean,
    default: false,
  },
  scrollable: {
    type: Boolean,
    default: false,
  },
  /** Horizontal scroll on mobile only; flex-wrap from lg up (resource index pages). */
  scrollableMobile: {
    type: Boolean,
    default: false,
  },
  sectionClass: {
    type: String,
    default: '',
  },
  reserveMinHeight: {
    type: Boolean,
    default: false,
  },
  /** Tighter bottom spacing on scrollable mobile (e.g. carousels). */
  compact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const isHeader = computed(() => props.variant === 'header');

const shouldRender = computed(() => {
  if (props.loading) return true;
  if (props.showAllOption && props.specialisations.length > 0) return true;
  return props.specialisations.length > 0;
});

const headingWrapperClass = computed(() => (
  isHeader.value ? 'bg-white flex items-center px-[4px] py-[8px] w-full' : 'px-1 py-2'
));

const headingClass = computed(() => (
  isHeader.value
    ? 'leading-[20px] text-[#737373] text-[14px] tracking-[1px] uppercase whitespace-nowrap font-sans font-semibold'
    : 'text-sm font-semibold uppercase tracking-[1px] text-[#737373]'
));

const isHorizontalScroll = computed(() => props.scrollable || props.scrollableMobile);

const tagsAreaClass = computed(() => {
  const classes = ['w-full'];

  if (props.scrollable) {
    classes.push(
      'relative left-1/2 mb-4 w-screen max-w-[100vw] -translate-x-1/2',
      'overflow-x-auto overscroll-x-contain',
      '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
    );
  } else if (props.scrollableMobile) {
    classes.push(
      props.compact
        ? 'relative left-1/2 mb-0 w-screen max-w-[100vw] -translate-x-1/2'
        : 'relative left-1/2 mb-4 w-screen max-w-[100vw] -translate-x-1/2',
      'overflow-x-auto overscroll-x-contain',
      '[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden',
      'lg:relative lg:left-auto lg:mb-0 lg:w-full lg:max-w-none lg:translate-x-0 lg:overflow-visible',
    );
  } else if (isHeader.value) {
    classes.push('mb-[16px]');
    if (props.reserveMinHeight) classes.push('min-h-[40px]');
  }

  return classes.join(' ');
});

const loadingWrapperClass = computed(() => (
  isHorizontalScroll.value
    ? 'w-full py-[12px] text-center text-gray pl-0'
    : 'w-full py-[12px] text-center text-gray'
));

const tagsRowClass = computed(() => {
  if (props.scrollable) {
    return 'flex min-w-full w-max flex-nowrap items-center gap-2 pb-1 pl-4 pr-4';
  }

  if (props.scrollableMobile) {
    return 'flex min-w-full w-max flex-nowrap items-center gap-2 pb-1 pl-4 pr-4 lg:min-w-0 lg:w-full lg:flex-wrap lg:pb-0 lg:pl-0 lg:pr-0';
  }

  if (isHeader.value) {
    return 'flex flex-wrap gap-[8px] items-start w-full';
  }

  return 'flex flex-wrap gap-2';
});

const pillLabelClass = computed(() => (
  isHeader.value
    ? 'leading-[16px] text-[14px] text-center whitespace-nowrap font-sans font-medium'
    : ''
));

function pillClass(isSelected) {
  const classes = [];

  if (isHeader.value) {
    classes.push(
      'flex gap-[8px] items-center justify-center px-[12px] py-[6px] rounded-[8px] transition-colors cursor-pointer',
      isSelected
        ? 'bg-dark text-white'
        : 'bg-[#f3f4f6] text-[#374151] hover:bg-code-medical-center-secondary',
    );
  } else {
    classes.push(
      'cursor-pointer rounded-lg px-3 py-1.5 text-sm font-medium transition-colors',
      isSelected
        ? 'bg-neutral-900 text-white'
        : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
    );
  }

  if (props.scrollable) {
    classes.push('shrink-0 whitespace-nowrap');
  } else if (props.scrollableMobile) {
    classes.push('shrink-0 whitespace-nowrap lg:shrink lg:whitespace-normal');
  }

  return classes;
}

function select(id) {
  emit('update:modelValue', id);
}
</script>
